import { FC, useContext, useState, useCallback } from "react";
import { Charity, einFormat } from "types/charity";
import { styled } from "@mui/material/styles";
import { warm } from "style/color";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { ReactComponent as SaveIcon } from "./../../assets/Icons/save.svg";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  useSaveCharityMutation,
  useUnsaveCharityMutation,
} from "hooks/use-services/charity.service";
import { AuthContext } from "providers/userProvider";
import { AuthData } from "types/auth";
import { USER_ROLE } from "types/account";
import { USER_STATUS } from "types/fund-account-invitation";

const CustomCard = styled(Card)({
  "& :hover": {
    backgroundColor: warm[500],
  },
});

interface CharityCardProps {
  charity: Charity;
  refresh: () => void;
  handleOpen: (value: boolean) => void;
  selectCharity: (ein: string) => void;
}

interface PopupWindow extends Window {
  document: Document;
}

const CharityCard: FC<CharityCardProps> = ({
  charity: _charity,
  refresh,
  handleOpen,
  selectCharity,
}) => {
  const [charity, setCharity] = useState(_charity);
  const { selectedAccount, userData, userRole } =
    useContext<AuthData>(AuthContext);
  const { mutate: saveCharity } = useSaveCharityMutation();
  const { mutate: unsaveCharity } = useUnsaveCharityMutation();

  // Save/Unsave charity toggle
  const onSaveUnSaveBtnClicked = (): void => {
    if (!selectedAccount?.fundAccountId) return;
    if (charity.isSavedByActiveFundAccount) {
      unsaveCharity(
        {
          fundAccountId: selectedAccount?.fundAccountId,
          charityEIN: charity.ein,
        },
        {
          onSuccess: () => {
            refresh();
          },
        }
      );
      setCharity({
        ...charity,
        isSavedByActiveFundAccount: false,
      });
    } else {
      saveCharity({
        fundAccountId: selectedAccount?.fundAccountId,
        charityEIN: charity.ein,
      });
      setCharity({
        ...charity,
        isSavedByActiveFundAccount: true,
      });
    }
  };

  // Open request page handler
  const openRequestPage = (ein: string): void => {
    if (userData?.user) {
      selectCharity(ein);
    } else {
      handleOpen(false);
    }
  };

  // --- Popup styles for redirect-only approach ---
  const getPopupStyles = (): string => `
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: #f8f9fa;
    }
    .neta-bar {
      background: #f5f5f5;
      color: #222;
      padding: 16px;
      font-size: 16px;
      border-bottom: 1px solid #ddd;
      display: flex;
      justify-content: space-between;
      align-items: center;
      position: sticky;
      top: 0;
      z-index: 1000;
    }
    .neta-bar button {
      background: #1976d2;
      color: #fff;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      transition: background 0.2s;
    }
    .neta-bar button:hover {
      background: #1565c0;
    }
    .redirect-container {
      margin-top: 60px;
      text-align: center;
      padding: 40px 20px;
      max-width: 600px;
      margin-left: auto;
      margin-right: auto;
    }
    .redirect-message {
      font-size: 18px;
      color: #333;
      margin-bottom: 20px;
      line-height: 1.5;
    }
    .redirect-url {
      color: #1976d2;
      word-break: break-all;
      margin-bottom: 30px;
      font-size: 14px;
      background: #f0f8ff;
      padding: 15px;
      border-radius: 8px;
      border: 1px solid #e3f2fd;
    }
    .redirect-link {
      display: inline-block;
      background: #1976d2;
      color: white;
      padding: 12px 24px;
      text-decoration: none;
      border-radius: 4px;
      font-size: 16px;
      transition: background 0.2s;
      margin-right: 10px;
    }
    .redirect-link:hover {
      background: #1565c0;
    }
    .countdown {
      font-size: 14px;
      color: #666;
      margin-top: 20px;
      line-height: 1.5;
    }
    .charity-info {
      background: white;
      padding: 20px;
      border-radius: 8px;
      margin-bottom: 30px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
    .charity-name {
      font-size: 20px;
      font-weight: bold;
      color: #333;
      margin-bottom: 10px;
    }
    .charity-details {
      color: #666;
      line-height: 1.6;
    }
  `;

  // --- Popup styles for iframe approach ---
  const getIframePopupStyles = (): string => `
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: #f8f9fa;
      display: flex;
      flex-direction: column;
      height: 100vh;
    }
    .neta-bar {
      background: #f5f5f5;
      color: #222;
      padding: 16px;
      font-size: 16px;
      border-bottom: 1px solid #ddd;
      display: flex;
      justify-content: space-between;
      align-items: center;
      position: sticky;
      top: 0;
      z-index: 1000;
      flex-shrink: 0;
    }
    .neta-bar button {
      background: #1976d2;
      color: #fff;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      transition: background 0.2s;
    }
    .neta-bar button:hover {
      background: #1565c0;
    }
    .iframe-container {
      flex: 1;
      position: relative;
    }
    .iframe-content {
      width: 100%;
      height: 100%;
      border: none;
      display: block;
    }
    .loading {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      color: #666;
    }
    .error {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      color: #d32f2f;
      padding: 20px;
      max-width: 400px;
    }
    .error-button {
      background: #1976d2;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 4px;
      cursor: pointer;
      margin-top: 15px;
    }
  `;

  // --- Simplified script that immediately shows redirect interface ---
  const getRedirectScript = (url: string, charityName: string): string => `
    class RedirectHandler {
      constructor(url, charityName) {
        this.url = url;
        this.charityName = charityName;
        this.init();
      }

      init() {
        this.createRedirectInterface();
        this.startCountdown();
      }

      createRedirectInterface() {
        const redirectDiv = document.createElement('div');
        redirectDiv.className = 'redirect-container';
        redirectDiv.innerHTML = \`
          <div class="charity-info">
            <div class="charity-name">\${this.charityName}</div>
            <div class="charity-details">You are about to visit this charity's external page for more information.</div>
          </div>
          <div class="redirect-message">
            For your security, external links open in a redirect page.<br>
            Click "Continue to Site" to proceed or wait for automatic redirect.
          </div>
          <div class="redirect-url">\${this.url}</div>
          <a href="\${this.url}" class="redirect-link" id="redirectLink">Continue to Site</a>
          <div class="countdown" id="countdown">
            Redirecting automatically in <span id="timer">5</span> seconds<br>
            <small>You can close this window after visiting the external site</small>
          </div>
        \`;
        document.body.appendChild(redirectDiv);
      }

      startCountdown() {
        let timeLeft = 10;
        const timerElement = document.getElementById('timer');
        const redirectLink = document.getElementById('redirectLink');

        const countdown = setInterval(() => {
          timeLeft--;
          if (timerElement) timerElement.textContent = timeLeft.toString();
          if (timeLeft <= 0) {
            clearInterval(countdown);
            window.location.href = this.url;
          }
        }, 1000);

        if (redirectLink) {
          redirectLink.addEventListener('click', (e) => {
            e.preventDefault();
            clearInterval(countdown);
            window.location.href = this.url;
          });
        }
      }
    }

    new RedirectHandler("${url}", "${charityName}");
  `;

  // --- Script for iframe approach ---
  const getIframeScript = (url: string, charityName: string): string => `
    class IframeHandler {
      constructor(url, charityName) {
        this.url = url;
        this.charityName = charityName;
        this.init();
      }

      init() {
        this.createIframeInterface();
      }

      createIframeInterface() {
        const container = document.createElement('div');
        container.className = 'iframe-container';

        const loading = document.createElement('div');
        loading.className = 'loading';
        loading.innerHTML = 'Loading ...';
        container.appendChild(loading);

        const iframe = document.createElement('iframe');
        iframe.className = 'iframe-content';
        iframe.src = this.url;
        iframe.title = this.charityName;

        iframe.onload = () => {
          loading.style.display = 'none';
        };

        iframe.onerror = () => {
          loading.style.display = 'none';
          const error = document.createElement('div');
          error.className = 'error';
          error.innerHTML = \`
            <h3>Unable to load content</h3>
            <p>This page cannot be displayed in a frame.</p>
            <button class="error-button" onclick="window.open('\${this.url}', '_blank')">
              Open in New Tab
            </button>
          \`;
          container.appendChild(error);
        };

        container.appendChild(iframe);
        document.body.appendChild(container);
      }
    }

    new IframeHandler("${url}", "${charityName}");
  `;

  // --- Generate popup HTML for redirect-only approach ---
  const generateRedirectPopupHTML = (url: string, charityName: string): string => `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Redirecting to ${charityName} - Neta</title>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style>${getPopupStyles()}</style>
      </head>
      <body>
        <div class="neta-bar">
          <span>You are leaving Neta to visit an external site</span>
          <button onclick="window.close()">Close</button>
        </div>
        <script>${getRedirectScript(url, charityName)}</script>
      </body>
    </html>
  `;

  // --- Generate popup HTML for iframe approach ---
  const generateIframePopupHTML = (url: string, charityName: string): string => `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${charityName} - Neta</title>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style>${getIframePopupStyles()}</style>
      </head>
      <body>
        <div class="neta-bar">
          <span>Viewing: ${charityName}</span>
          <div>
            <button onclick="window.open('${url}', '_blank')">Open in New Tab</button>
            <button onclick="window.close()" style="margin-left: 10px;">Close</button>
          </div>
        </div>
        <script>${getIframeScript(url, charityName)}</script>
      </body>
    </html>
  `;

  // --- Create popup with redirect-only approach ---
  const createRedirectPopup = useCallback((url: string, charityName: string): void => {
    try {
      const popup: PopupWindow | null = window.open(
        "",
        "_blank",
        "width=600,height=500,scrollbars=yes,resizable=yes"
      ) as PopupWindow | null;

      if (!popup) {
        // Popup blocked fallback - directly open URL
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }

      popup.document.open();
      popup.document.write(generateRedirectPopupHTML(url, charityName));
      popup.document.close();
      popup.focus();
    } catch (error) {
      console.error("Redirect popup creation failed:", error);
      // Direct fallback
      window.open(url, "_blank", "noopener,noreferrer");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Create popup with iframe approach ---
  const createIframePopup = useCallback((url: string, charityName: string): void => {
    try {
      const popup: PopupWindow | null = window.open(
        "",
        "_blank",
        "width=900,height=700,scrollbars=yes,resizable=yes"
      ) as PopupWindow | null;

      if (!popup) {
        // Popup blocked fallback - directly open URL
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }

      popup.document.open();
      popup.document.write(generateIframePopupHTML(url, charityName));
      popup.document.close();
      popup.focus();
    } catch (error) {
      console.error("Iframe popup creation failed:", error);
      // Direct fallback
      window.open(url, "_blank", "noopener,noreferrer");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Handler for "More info" button ---
  const handleMoreInfoClick = useCallback(() => {
    let url: string = charity.recommended
      ? charity.organizationUrl
      : `https://www.charitynavigator.org/ein/${charity.ein}`;



    // Use redirect popup for recommended charities, iframe for others
    if (charity.recommended) {
      if (!/^https?:\/\//i.test(url)) {
        url = 'http://' + url;
      }
      createRedirectPopup(url, charity.name);
    } else {
      createIframePopup(url, charity.name);
    }
  }, [charity.ein, charity.organizationUrl, charity.recommended, charity.name, createRedirectPopup, createIframePopup]);

  return (
    <CustomCard>
      <CardContent sx={{ "&:last-child": { pb: 5 } }}>
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems={"center"}
          >
            <Typography
              variant="smallCopy"
              sx={{
                color: "neutral.main",
              }}
            >
              {charity.country === "USA" ? einFormat(charity.ein) : charity.ein}
            </Typography>

            {userData?.user && (
              <IconButton
                className={
                  charity.isSavedByActiveFundAccount
                    ? "charities-saved"
                    : "charities-unsaved"
                }
                size="small"
                onClick={onSaveUnSaveBtnClicked}
              >
                <SaveIcon />
              </IconButton>
            )}
          </Stack>
        </Box>
        <Box sx={{ height: "120px" }}>
          <Typography
            gutterBottom
            sx={{
              color: "neutral.main",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: "2",
              WebkitBoxOrient: "vertical",
              whiteSpace: "pre-wrap",
            }}
            variant="subtitle1"
          >
            {charity.name}
          </Typography>
          <Typography
            paragraph
            sx={{
              color: "neutral.main",
            }}
            variant="smallCopyBold"
          >
            {charity.street} {charity.street2},<br />
            {charity.city}, {charity.state} {charity.zip}, {charity.country}
          </Typography>
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems={"center"}
          sx={{
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            justifyContent: {
              xs: "flex-start",
              sm: "space-between",
            },
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            gap: {
              xs: 2,
              sm: 0,
            }
          }}
        >
          <Button
            variant="contained"
            color="primary"
            disabled={
              userRole === USER_ROLE.VIEWER ||
              userData?.user?.status === USER_STATUS.PENDING
            }
            onClick={() => openRequestPage(charity.ein)}
          >
            Recommend a grant
          </Button>
          <Button
            startIcon={<LaunchIcon />}
            variant="text"
            color="primary"
            className="charites-more-info"
            onClick={handleMoreInfoClick}
            disabled={charity.recommended && !charity.organizationUrl}
          >
            More info
          </Button>
        </Stack>
      </CardContent>
    </CustomCard>
  );
};

export default CharityCard;
